import type { TechnicalSpecificationState } from '../../services/projectAutofillDraftService.ts'
import type {
  DocumentationFileIds,
  DocumentationSlots,
  DraftMetadataFileEntry
} from './projectAutofillDraftUtils.ts'
import {
  DOCUMENTATION_SLOT_DEFINITIONS,
  draftMetadataFileLabel,
  isFullPageSelection,
  normalizePageList
} from './projectAutofillDraftUtils.ts'
import type {
  DocumentationReviewContext,
  MetricEligibility,
  QuotationEligibility,
  TechnicalSpecEligibility
} from './projectAutofillWorkflow.ts'
import {
  isMetricOnlyReviewScreen,
  isQuotationTabEnabled,
  rubroBudgetLegajoInPdf,
  rubroMetricReadyFromRow,
  rubroTechnicalSpecLegajoInPdf,
  technicalSpecHasUsableContent
} from './projectAutofillWorkflow.ts'

export type DocumentationSlotKey = (typeof DOCUMENTATION_SLOT_DEFINITIONS)[number]['key']

const summarizePagesShort = (pages: number[] | undefined, maxItems = 10): string => {
  const norm = normalizePageList(pages)
  if (!norm?.length) return ''
  if (norm.length <= maxItems) return norm.join(', ')
  return `${norm.slice(0, maxItems).join(', ')}… (+${norm.length - maxItems})`
}

export type SlotOriginSummaryInput = {
  slot: DocumentationSlotKey
  slots: DocumentationSlots
  fileIds: DocumentationFileIds
  draftFiles: DraftMetadataFileEntry[]
  maxPage?: number | null
  pdfBaseUrl?: string
}

export function formatSlotOriginSummary(input: SlotOriginSummaryInput): {
  text: string
  hasOrigin: boolean
  pdfHref?: string
} {
  const { slot, slots, fileIds, draftFiles, maxPage, pdfBaseUrl } = input
  const pages = slots[slot]
  const hasPages = Array.isArray(pages) && pages.length > 0
  const fid = fileIds[slot]
  const hasFile = typeof fid === 'string' && fid.trim().length > 0
  const basePdf = pdfBaseUrl?.trim() || ''

  if (hasFile) {
    const entry = draftFiles.find((f) => String(f.id ?? '') === String(fid))
    const label = entry ? draftMetadataFileLabel(entry) : fid
    return {
      text: `Archivo adjunto: ${label}`,
      hasOrigin: true,
      pdfHref: entry?.storageUrl?.trim() || undefined
    }
  }

  if (
    maxPage != null &&
    Number.isFinite(maxPage) &&
    maxPage >= 1 &&
    isFullPageSelection(pages, maxPage)
  ) {
    return {
      text: `Todo el PDF del pliego (${maxPage} ${maxPage === 1 ? 'página' : 'páginas'})`,
      hasOrigin: true,
      pdfHref: basePdf || undefined
    }
  }

  if (hasPages) {
    const short = summarizePagesShort(pages)
    return {
      text: short ? `Páginas del legajo: ${short}` : 'Páginas asignadas',
      hasOrigin: true,
      pdfHref: basePdf || undefined
    }
  }

  return { text: 'Sin origen en el legajo', hasOrigin: false }
}

export type RubroPublishChip = {
  id: string
  label: string
  tone: 'ok' | 'warn' | 'muted' | 'info'
}

export function buildRubroPublishStatusChips(
  row: {
    metricEligibility?: MetricEligibility | null
    quotationEligibility?: QuotationEligibility | null
    technicalSpecEligibility?: TechnicalSpecEligibility | null
    technicalSpecificationState?: TechnicalSpecificationState | null
    documentationSlots?: DocumentationSlots
    documentationFileIds?: DocumentationFileIds
    metricSheetState?: { rows?: { cells?: Record<string, unknown> }[] } | null
    budgetSheetState?: { rows?: { cells?: Record<string, unknown> }[] } | null
  },
  documentationReview: DocumentationReviewContext
): RubroPublishChip[] {
  const chips: RubroPublishChip[] = []
  const metricReady = rubroMetricReadyFromRow(row)

  if (documentationReview.isProcessing) {
    chips.push({ id: 'pipeline', label: 'Analizando documento…', tone: 'info' })
    return chips
  }

  if (isMetricOnlyReviewScreen(documentationReview.reviewStage)) {
    chips.push({ id: 'metric-only', label: 'Re-analizar para cotización', tone: 'warn' })
  }

  const te = row.technicalSpecEligibility
  const specContent = technicalSpecHasUsableContent(row.technicalSpecificationState)
  if (rubroTechnicalSpecLegajoInPdf(row)) {
    chips.push({ id: 'technical', label: 'Especificaciones: PDF legajo', tone: 'ok' })
  } else if (te === 'generation_failed' && !specContent) {
    chips.push({ id: 'technical', label: 'Especificaciones: revisar', tone: 'warn' })
  } else if (te === 'user_edited' || (te === 'generated' && specContent)) {
    chips.push({
      id: 'technical',
      label: te === 'user_edited' ? 'Especificaciones editadas' : 'Especificaciones sugeridas',
      tone: 'ok'
    })
  } else if (te === 'locked' || documentationReview.isProcessing) {
    chips.push({ id: 'technical', label: 'Especificaciones al terminar análisis', tone: 'muted' })
  } else if (!isQuotationTabEnabled(documentationReview.reviewStage)) {
    chips.push({ id: 'technical', label: 'Especificaciones pendientes', tone: 'muted' })
  } else {
    chips.push({ id: 'technical', label: 'Especificaciones pendientes', tone: 'warn' })
  }

  chips.push({
    id: 'metric',
    label: metricReady ? 'Cómputo listo' : 'Cómputo pendiente',
    tone: metricReady ? 'ok' : 'warn'
  })

  if (!isQuotationTabEnabled(documentationReview.reviewStage)) {
    chips.push({ id: 'quote', label: 'Cotización al terminar análisis', tone: 'muted' })
    return chips
  }

  const q = row.quotationEligibility
  if (q === 'skipped_no_metric') {
    chips.push({ id: 'quote', label: 'Sin cotización en este rubro', tone: 'muted' })
  } else if (q === 'quotation_generation_failed') {
    chips.push({ id: 'quote', label: 'Cotización: revisar', tone: 'warn' })
  } else if (rubroBudgetLegajoInPdf(row) && !row.budgetSheetState) {
    chips.push({ id: 'quote', label: 'Cotización vía PDF', tone: 'ok' })
  } else if (row.budgetSheetState) {
    chips.push({ id: 'quote', label: 'Planilla de cotización', tone: 'ok' })
  } else if (q === 'locked' || q === 'pending') {
    chips.push({ id: 'quote', label: 'Cotización bloqueada', tone: 'muted' })
  } else {
    chips.push({ id: 'quote', label: 'Cotización pendiente', tone: 'warn' })
  }

  return chips
}

export function buildAuxiliaryLegajoChips(
  slots: DocumentationSlots,
  fileIds: DocumentationFileIds,
  options?: { omitTechnicalSpecifications?: boolean }
): RubroPublishChip[] {
  const aux: DocumentationSlotKey[] = options?.omitTechnicalSpecifications
    ? ['planos']
    : ['planos', 'technicalSpecifications']
  return aux
    .map((key) => {
      const def = DOCUMENTATION_SLOT_DEFINITIONS.find((d) => d.key === key)
      const pages = slots[key]
      const hasPages = Array.isArray(pages) && pages.length > 0
      const fid = fileIds[key]
      const hasFile = typeof fid === 'string' && fid.trim().length > 0
      if (!hasPages && !hasFile) return null
      const n = normalizePageList(pages)?.length ?? 0
      const label = hasFile
        ? `${def?.chipLabel ?? key}: archivo`
        : `${def?.chipLabel ?? key}: ${n} pág.`
      return { id: key, label, tone: 'info' as const }
    })
    .filter((x): x is RubroPublishChip => x != null)
}
